import { useState, useEffect, useCallback, useMemo } from "react";
import { usePrivy, useWallets, useSendTransaction } from "@privy-io/react-auth";
import { ethers } from "ethers";
import { getChainConfig } from "@/config/contracts";
import { useMockAuth } from "@/config/privy";
import { PolygonClient } from "@/lib/polygon";
import type { SponsoredSendFn } from "@/lib/polygon";
import { createSupabaseTxLogger, type TxContextSnapshot } from "@/lib/txContext";
import { useAuthStore } from "@/stores/useAuthStore";
import type { OnchainLinkedWallet } from "@/lib/types";
import { useDebugStore } from "@/stores/useDebugStore";
import { debugLog } from "@/lib/debugLog";
import { contractLabelFor } from "@/lib/debugContracts";
import { getActiveChainId, getEnvChainId, isMainnetChainId } from "@/config/activeChain";

// When set, WebSocket receipts replace HTTP-poll receipts on the sponsored
// send path. Cuts ~1–2s off every tx; safe to leave unset (we fall back to
// the signer's provider).
//
// Chain-scoped on purpose: the socket must be on the same network the tx is
// sent to. A single shared endpoint would silently keep listening to the old
// chain after the network switch, and a receipt that never arrives on that
// socket is indistinguishable from one that is merely slow.
function resolveWsRpcUrl(): string | undefined {
  const perChain = isMainnetChainId(getActiveChainId())
    ? import.meta.env.VITE_POLYGON_MAINNET_WSS_URL
    : import.meta.env.VITE_POLYGON_AMOY_WSS_URL;
  if (typeof perChain === "string" && perChain) return perChain;

  // Legacy single-endpoint config. Only honour it on the chain this
  // deployment was built for -- on a switched session it points at the wrong
  // network, and no WS receipts is strictly better than wrong ones.
  const legacy = import.meta.env.VITE_POLYGON_WSS_URL as string | undefined;
  if (legacy && getActiveChainId() === getEnvChainId()) return legacy;
  if (legacy) {
    console.warn(
      "[usePolygonClient] VITE_POLYGON_WSS_URL is a single-chain endpoint and the app is running on a switched chain; using HTTP receipts instead. Set VITE_POLYGON_AMOY_WSS_URL / VITE_POLYGON_MAINNET_WSS_URL to keep WebSocket receipts.",
    );
  }
  return undefined;
}

const wsRpcUrl = resolveWsRpcUrl();

// One shared WebSocketProvider per page load. Module-scope (not useMemo) so
// React 18 Strict Mode's double-invocation in dev doesn't tear down the
// connection mid-flight — every PolygonClient rebuild reuses the same socket,
// and the browser closes it when the tab closes.
let sharedReceiptProvider: ethers.WebSocketProvider | undefined;
let receiptProviderInitialized = false;
function getReceiptProvider(): ethers.WebSocketProvider | undefined {
  if (!wsRpcUrl) return undefined;
  if (!receiptProviderInitialized) {
    receiptProviderInitialized = true;
    try {
      sharedReceiptProvider = new ethers.WebSocketProvider(wsRpcUrl);
    } catch (err) {
      console.warn("[usePolygonClient] WebSocketProvider creation failed:", err);
      sharedReceiptProvider = undefined;
    }
  }
  return sharedReceiptProvider;
}

/**
 * Returns a PolygonClient connected to the user's Privy embedded wallet,
 * or null if the wallet is not yet ready or we're in mock auth mode.
 *
 * The client is wired with a TxContextSnapshot + TxLogger so every tx issued
 * via it gets logged to onchain_txs. Fire-and-forget — failures to log never
 * block a user tx.
 */
function usePolygonClientReal(): PolygonClient | null {
  const [client, setClient] = useState<PolygonClient | null>(null);
  const { user } = usePrivy();
  const { wallets } = useWallets();
  const { sendTransaction } = useSendTransaction();
  const storedWallet = useAuthStore((s) => s.session?.walletAddress ?? null);

  const embeddedWallet = wallets.find((w) => w.walletClientType === "privy");
  const walletAddress = embeddedWallet?.address;
  const privyDid = user?.id ?? null;
  const debugEnabled = useDebugStore((s) => s.enabled);

  const sponsoredSend = useCallback<SponsoredSendFn>(
    async (to, data, value) => {
      const result = await sendTransaction(
        { to, data, ...(value !== undefined ? { value } : {}) },
        { sponsor: true },
      );
      return result.hash;
    },
    [sendTransaction],
  );

  const txLogger = useMemo(() => createSupabaseTxLogger(), []);

  // Module-scoped singleton — see comment at top of file.
  const receiptProvider = getReceiptProvider();

  // JSON-stringified snapshot of linked wallets so deep-equality checks don't
  // re-create the client on every Privy render.
  const linkedWalletsKey = useMemo(
    () => JSON.stringify(wallets.map((w) => ({ a: w.address, t: w.walletClientType }))),
    [wallets],
  );

  useEffect(() => {
    if (!embeddedWallet || !walletAddress) {
      setClient(null);
      return;
    }

    let cancelled = false;

    async function init() {
      try {
        const chainConfig = getChainConfig();
        await embeddedWallet!.switchChain(chainConfig.chainId);
        const eip1193 = await embeddedWallet!.getEthereumProvider();
        const provider = new ethers.BrowserProvider(eip1193);
        const signer = await provider.getSigner();

        if (cancelled) return;

        const linkedWallets: OnchainLinkedWallet[] = wallets.map((w) => ({
          address: w.address,
          walletClientType: w.walletClientType,
        }));

        const context: TxContextSnapshot | undefined = privyDid
          ? {
              privyDid,
              uiResolvedSigner: walletAddress!,
              storedWallet,
              linkedWallets,
              chainId: chainConfig.chainId,
              sponsored: true,
            }
          : undefined;

        const onSend = debugEnabled
          ? ({ to, method, txHash }: { to: string; method: string; txHash: string }) => {
              const contract = contractLabelFor(to);
              debugLog("info", "chain", `${contract}.${method}`, { contract, to, method, txHash });
            }
          : undefined;

        console.info(
          "[usePolygonClient] built PolygonClient",
          {
            hasContext: !!context,
            hasLogger: true,
            privyDid,
            uiResolvedSigner: walletAddress,
            storedWallet,
            linkedWalletCount: linkedWallets.length,
            chainId: chainConfig.chainId,
          },
        );
        setClient(
          new PolygonClient(signer, sponsoredSend, context, txLogger, onSend, receiptProvider),
        );
      } catch (err) {
        console.error("[usePolygonClient] Failed to initialize:", err);
        if (!cancelled) setClient(null);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- walletAddress and linkedWalletsKey stand in for embeddedWallet and wallets, rebuilt every render
  }, [walletAddress, sponsoredSend, privyDid, storedWallet, linkedWalletsKey, txLogger, debugEnabled, receiptProvider]);

  return client;
}

function usePolygonClientMock(): PolygonClient | null {
  return null;
}

// useMockAuth is a module-level constant resolved once at load time. Selecting
// the implementation here keeps Rules of Hooks intact: each variant calls its
// hooks unconditionally and is referentially stable for React's component
// identity.
export const usePolygonClient: () => PolygonClient | null = useMockAuth
  ? usePolygonClientMock
  : usePolygonClientReal;
